import { watch } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { isNonNullable } from '@/utils/nullability-helpers';
import { getCurrentUser } from '@/services/user-service';
import {
    applyOrganizationDefaultLanguage,
    currentLanguage,
    languagePreferenceIsExplicit,
    loadLanguageAsync,
    markLanguagePreferenceExplicit,
    clearLanguagePreferenceExplicit,
} from '@/i18n';

/**
 * Owns runtime UI-language resolution for the app shell: hierarchy is
 * user preference (`users.preferred_language`) -> current org default -> platform default.
 *
 * Two concerns, both driven off the auth store:
 *
 * 1. Hydrate the signed-in user's saved preference and reconcile the per-browser "explicit choice"
 *    flag the first time each user becomes available:
 *    - non-null preference → mark the flag (so the org default never overrides it) and switch the
 *      UI to it if it differs from the current (browser-seeded) language.
 *    - null preference → clear the flag, so a stale flag from an earlier session/user on this
 *      browser doesn't keep blocking org-default resolution.
 *
 * 2. Apply the current organization's default language for users without an explicit preference.
 *    Re-runs when the org's `defaultLanguage` / `languageRestrictions` change, and when the
 *    explicit flag flips (e.g. concern 1 clearing a stale flag after the org metadata is already
 *    in place). `applyOrganizationDefaultLanguage` no-ops when a preference is set.
 *
 * Pre-login pages (invite / join) call `applyOrganizationDefaultLanguage` directly — they render
 * before this composable's watcher can react to a freshly fetched org.
 *
 * Call once, high in the tree (App.vue).
 */
export function useLanguageResolution(): void {
    const { user, currentOrganization } = useAuthStore();

    // --- 1. user preference hydration ---
    // Track the id we last reconciled rather than a plain boolean, so an in-session account switch
    // (sign-out is a router.push, not a reload) re-runs for the new user.
    let hydratedUserId: string | undefined;

    watch(
        () => user.value?.id,
        async (userId) => {
            if (!isNonNullable(userId) || userId === hydratedUserId) {
                return;
            }

            let profile;
            try {
                profile = await getCurrentUser();
            } catch (error) {
                // Transient failure — don't latch, so the next trigger retries.
                console.warn('[i18n] failed to load user language preference', error);
                return;
            }

            hydratedUserId = userId;

            const preferred = profile?.preferredLanguage;
            if (!isNonNullable(preferred)) {
                clearLanguagePreferenceExplicit();
                return;
            }

            markLanguagePreferenceExplicit();
            if (preferred !== currentLanguage.value) {
                await loadLanguageAsync(preferred);
            }
        },
        { immediate: true },
    );

    // --- 2. organization default ---
    watch(
        [
            () => currentOrganization.value?.metadata?.defaultLanguage,
            () => currentOrganization.value?.metadata?.languageRestrictions,
            languagePreferenceIsExplicit,
        ],
        ([defaultLanguage, languageRestrictions]) => {
            void applyOrganizationDefaultLanguage(defaultLanguage, languageRestrictions);
        },
        { immediate: true },
    );
}
